
import { useEffect, useState, useRef } from 'react';
import { analytics } from '@/services/analytics/analyticsService';

export interface UseScrollTrackingOptions {
  // Thresholds to track (percentage values)
  thresholds?: number[];
  // How frequently to check scroll position (ms)
  throttleMs?: number;
  // Whether to track only unique thresholds or repeatedly
  trackOnlyUnique?: boolean;
}

/**
 * Hook to track scroll depth on the page
 */
export function useScrollTracking({
  thresholds = [25, 50, 75, 100],
  throttleMs = 500,
  trackOnlyUnique = true,
}: UseScrollTrackingOptions = {}) {
  const trackedThresholds = useRef<Set<number>>(new Set());
  const [maxScrollPercentage, setMaxScrollPercentage] = useState(0);
  const throttleTimeout = useRef<number | null>(null);
  
  useEffect(() => {
    const calculateScrollPercentage = () => {
      const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (documentHeight <= 0) return 0;
      return Math.floor((window.scrollY / documentHeight) * 100);
    };

    const handleScroll = () => {
      // Skip if we're throttling
      if (throttleTimeout.current !== null) return;
      
      throttleTimeout.current = window.setTimeout(() => {
        throttleTimeout.current = null;
        
        const currentScrollPercentage = calculateScrollPercentage();
        if (currentScrollPercentage > maxScrollPercentage) {
          setMaxScrollPercentage(currentScrollPercentage);
          
          // Check which thresholds have been crossed
          for (const threshold of thresholds) {
            if (currentScrollPercentage >= threshold) {
              if (!trackOnlyUnique || !trackedThresholds.current.has(threshold)) {
                trackedThresholds.current.add(threshold);
                analytics.trackScrollDepth(threshold);
              }
            }
          }
        }
      }, throttleMs);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimeout.current !== null) {
        clearTimeout(throttleTimeout.current);
      }
    };
  }, [thresholds, throttleMs, trackOnlyUnique, maxScrollPercentage]);

  return {
    maxScrollPercentage
  };
}
